import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { hasCustomerItem } from '../../../store/customer/selector';
import { useOrderActions } from '../../../store/order/actions';
import { orderItemState } from '../../../store/order/atom';
import { hasOrderItem } from '../../../store/order/selectors';
import { hasProductItem } from '../../../store/product/selectors';
import { SelectedProduct } from '../../../store/product/types';
import { SectionLocked } from '../components/SectionLocked';

import { MobileSwitches } from './MobileSwitches';
import { ProveOrderOrigin } from './ProveOrderOrigin';
import { ValueAdjustedCheck } from './ValueAdjustedCheck';

interface ChannelOption {
  label: string;
  value: string;
  isInvalid?: boolean;
  invalidMessage?: string;
  disabled?: boolean;
}

export interface OrderStepMobileProps {
  channelOptions: ChannelOption[];
  onChannelSelectChange: (value: string, selected: boolean) => void;
  selectedChannels?: string[];
  productTypeId: SelectedProduct['id'];
  sendingChannelMessage?: string;
}

export const OrderStepMobile: React.FC<OrderStepMobileProps> = ({
  channelOptions,
  onChannelSelectChange,
  selectedChannels,
  productTypeId,
  sendingChannelMessage,
}) => {
  const orderItem = useRecoilValue(orderItemState);
  const hasProduct = useRecoilValue(hasProductItem);
  const hasCustomer = useRecoilValue(hasCustomerItem);
  const hasOrder = useRecoilValue(hasOrderItem);
  const { setOrderInteractionChannelAudit } = useOrderActions();

  if (!hasProduct || !hasCustomer) {
    return <SectionLocked title="Conclua sua boleta" />;
  }

  return (
    <VStack mx="inline.xxxs" space="1px">
      <HStack p="inline.xxxs" bg="neutral.white" borderTopRadius={BoxRadius.sm}>
        <UI.Text variant="semibold" color="neutral.black">
          Conclua sua boleta
        </UI.Text>
      </HStack>

      <VStack
        mt={0.5}
        px="inline.xxxs"
        py="stack.xxs"
        borderBottomRadius={BoxRadius.sm}
        bg="neutral.white"
        space="stack.xxxs">
        {sendingChannelMessage && (
          <Stack w="full" pb="stack.xxxs">
            <UI.Notice
              type="warning"
              backgroundColor={Utils.ColorToken.WithOpacity(
                'feedback.attention.lightest',
                'opacity.medium'
              )}
              textObjects={[
                { text: 'Atenção: ', variant: 'bold' },
                { text: sendingChannelMessage, variant: 'regular' },
              ]}
            />
          </Stack>
        )}
        <ProveOrderOrigin
          productTypeId={productTypeId}
          handleUpdateInteractionChannelAudit={setOrderInteractionChannelAudit}
          hideSeparator
        />
        <ValueAdjustedCheck />

        <VStack space="inline.xxxs">
          <MobileSwitches
            channelOptions={channelOptions}
            onChannelSelectChange={onChannelSelectChange}
            selectedChannels={selectedChannels}
          />
          <HStack justifyContent="space-between">
            <UI.Text color="neutral.black">Data de Envio</UI.Text>
            <UI.Text color="neutral.black">{hasOrder ? orderItem.date : '00/00/0000'}</UI.Text>
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  );
};
